import React, {
  createContext, useContext, useMemo, useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { USER_CONSTANTS } from 'src/constants/user';
import { useRewardedAd } from './useRewardedAd';
import RewardedSuccessModal from './RewardedSuccessModal';

/**
 * Single-instance rewarded-video state machine shared across surfaces.
 *
 * Two separate callbacks, fired at different moments:
 *
 *   onCooldownCleared — SILENT. Runs the instant /confirm credits the
 *     reward, which is ~15s into the ad while the fullscreen GPT iframe
 *     still covers the page. It dispatches USER_CONSTANTS.UPDATE
 *     { timeToFreeVote: null } and patches localStorage so the cooldown is
 *     gone, but shows NO visible feedback (the user can't see anything
 *     behind the ad).
 *
 *   onSuccessVisible — VISIBLE. Runs only after the ad iframe has closed,
 *     so the user actually sees the confirmation. Opens RewardedSuccessModal
 *     (a centered "Free vote unlocked!" card). This replaced an earlier
 *     snackbar that users found too easy to miss — "I closed the ad and
 *     nothing happened, I can't tell it worked."
 */

const RewardedAdContext = createContext(null);

export function RewardedAdProvider({ children }) {
  const dispatch = useDispatch();
  const [success, setSuccess] = useState({ open: false, petName: '' });

  const onCooldownCleared = useCallback(() => {
    dispatch({
      type: USER_CONSTANTS.UPDATE,
      payload: { timeToFreeVote: null },
    });
    // Keep localStorage in sync so a refresh between this dispatch and the
    // next /users/me fetch doesn't briefly show the stale cooldown timer.
    try {
      window.localStorage.setItem('timeToFreeVote', '0');
      const userDataRaw = window.localStorage.getItem('userData');
      if (userDataRaw) {
        const userData = JSON.parse(userDataRaw);
        userData.timeToFreeVote = null;
        window.localStorage.setItem('userData', JSON.stringify(userData));
      }
    } catch (e) {
      // localStorage unavailable (private mode) or userData corrupt — ignore
    }
  }, [dispatch]);

  // Visible confirmation, fired by the hook only after the ad iframe closes
  // so the user is actually looking at the page when it appears. Receives
  // the pet watched-for so the modal can name it.
  const onSuccessVisible = useCallback((pet) => {
    setSuccess({ open: true, petName: (pet && pet.petName) || '' });
  }, []);

  const machine = useRewardedAd({ onCooldownCleared, onSuccessVisible });

  // Stable value reference: only the machine snapshot changes between renders.
  const value = useMemo(() => machine, [
    machine.status,
    machine.dailyRemaining,
    machine.dailyUsed,
    machine.dailyCap,
    machine.isAvailable,
    machine.enabled,
    machine.startAd,
    machine.refreshCap,
    machine.hasRecentNoFill,
  ]);

  return (
    <RewardedAdContext.Provider value={value}>
      {children}
      <RewardedSuccessModal
        open={success.open}
        petName={success.petName}
        onClose={() => setSuccess((s) => ({ ...s, open: false }))}
      />
    </RewardedAdContext.Provider>
  );
}

RewardedAdProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useRewardedAdContext() {
  return useContext(RewardedAdContext);
}
